import {merge} from 'rxjs';
import {filter, map, throttleTime} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {tradePair$} from '@/state/trade-pair';
import {bookCaches$} from '@/book/book-cache';
import type {DepthItem, TradePair, SpotPair, BookCache, BookCaches, Depth} from '@/types';

const resetAction$ = tradePair$.pipe(map(({symbol}: TradePair): Depth => ({reset: true, symbol, bids: [], asks: []})));
const newDepthAction$ = bookCaches$.pipe(throttleTime(1000), map(convertNewDepthAction), filter(Boolean));

export const depth$ = merge<Depth[]>(resetAction$, newDepthAction$);

function convertNewDepthAction({tradePair, bids, bidPriceEps, asks, askPriceEps}: BookCaches): Depth {
  if (!tradePair) {
    return null;
  }
  const {symbol, pricePrecision, priceScale: perpetualPriceScale, type} = tradePair;
  const {
    baseQtyPrecision: qtyPrecision = 0,
    baseQtyScale: qtyScale = 0,
    quoteQtyScale: spotPriceScale = 0,
  } = tradePair as SpotPair;
  const priceScale = type === 'Spot' ? spotPriceScale : perpetualPriceScale;
  const newBids = calcDepthOrders(bids, bidPriceEps, pricePrecision, priceScale, qtyPrecision, qtyScale);
  const newAsks = calcDepthOrders(asks, askPriceEps, pricePrecision, priceScale, qtyPrecision, qtyScale);
  return {reset: false, symbol, bids: newBids, asks: newAsks};
}

function calcDepthOrders(
  bookCache: BookCache,
  priceEps: number[],
  pricePrecision: number,
  priceScale: number,
  qtyPrecision: number,
  qtyScale: number
): DepthItem[] {
  const acc = priceEps.reduce<{total: number; items: DepthItem[]}>(
    (p, priceEp: number) => {
      const size = bookCache.get(priceEp);
      const item: DepthItem = {
        priceEp,
        price: numUtil.floor(priceEp, pricePrecision, priceScale),
        size,
        total: qtyPrecision === 0 ? p.total : Number(numUtil.floor(p.total, qtyPrecision, qtyScale)),
      };
      return {total: p.total + size, items: [...p.items, item]};
    },
    {total: 0, items: []}
  );
  return acc.items;
}
